import React, { useCallback, useState, useEffect } from 'react';
import {
  Modal,
  Typography,
  Tag,
  Space,
  Button,
  notification,
  Spin,
  Alert,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  LoadingOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { StrategyGuideStatuses } from '@aims/shared/strategy-guides';
import sharedSettings from '@aims/shared/sharedSettings';
import { reviewStrategyGuideMutation } from './constants';

const { Text } = Typography;

function StrategyGuidePreviewModal({
  visible,
  onCancel,
  strategyGuide,
  onReviewComplete,
}) {
  const [loading, setLoading] = useState(true);
  const [reviewStrategyGuide, { loading: updating }] = useMutation(
    reviewStrategyGuideMutation,
  );

  // Reset loading state when modal opens or strategyGuide changes
  useEffect(() => {
    if (visible && strategyGuide?.file?.previewUrl) {
      setLoading(true);
      // Fallback timeout to hide loading indicator after 3 seconds
      const timer = setTimeout(() => {
        setLoading(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [visible, strategyGuide]);

  const handleApprove = useCallback(async () => {
    if (!strategyGuide) return;
    try {
      await reviewStrategyGuide({
        variables: {
          review: {
            _id: strategyGuide._id,
            status: 'APPROVED',
          },
        },
        refetchQueries: [
          'LatestStrategyGuideForContact',
          'StrategyGuideVersionsForContact',
        ],
        awaitRefetchQueries: true,
      });
      notification.success({
        message: 'Success',
        description: 'Strategy guide approved.',
      });
      if (onReviewComplete) {
        onReviewComplete();
      }
      onCancel();
    } catch (err) {
      notification.error({
        message: 'Error',
        description: 'Failed to approve strategy guide.',
      });
    }
  }, [strategyGuide, reviewStrategyGuide, onReviewComplete, onCancel]);

  const handleReject = useCallback(async () => {
    if (!strategyGuide) return;
    try {
      await reviewStrategyGuide({
        variables: {
          review: {
            _id: strategyGuide._id,
            status: 'PENDING_CHANGES',
          },
        },
        refetchQueries: [
          'LatestStrategyGuideForContact',
          'StrategyGuideVersionsForContact',
        ],
        awaitRefetchQueries: true,
      });
      notification.success({
        message: 'Success',
        description: 'Strategy guide marked as pending changes.',
      });
      if (onReviewComplete) {
        onReviewComplete();
      }
      onCancel();
    } catch (err) {
      notification.error({
        message: 'Error',
        description: 'Failed to update strategy guide status.',
      });
    }
  }, [strategyGuide, reviewStrategyGuide, onReviewComplete, onCancel]);

  if (!strategyGuide) {
    return null;
  }

  // Use previewUrl for inline display, fall back to url for download
  const previewUrl = strategyGuide.file?.previewUrl;
  const downloadUrl = strategyGuide.file?.url;

  return (
    <Modal
      title={
        <Space>
          <Text>Strategy Guide Preview</Text>
          <Tag color={sharedSettings.colors.primary}>
            v{strategyGuide.version}
          </Tag>
          <Tag color={StrategyGuideStatuses[strategyGuide.status]?.color}>
            {StrategyGuideStatuses[strategyGuide.status]?.label}
          </Tag>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <Button onClick={onCancel}>Close</Button>
          </div>
          <Space>
            <Button
              danger
              icon={<CloseOutlined />}
              onClick={handleReject}
              loading={updating}
              disabled={strategyGuide.status === 'PENDING_CHANGES'}
            >
              Request Changes
            </Button>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={handleApprove}
              loading={updating}
              disabled={strategyGuide.status === 'APPROVED'}
              style={{
                backgroundColor: sharedSettings.colors.success,
                borderColor: sharedSettings.colors.success,
              }}
            >
              Approve
            </Button>
          </Space>
        </div>
      }
      width="90%"
      style={{ top: 20 }}
      styles={{ body: { height: 'calc(100vh - 200px)', padding: 0 } }}
    >
      {!previewUrl ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <Text type="secondary">
            No file URL available. The file may not have been uploaded yet.
          </Text>
        </div>
      ) : (
        <div
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Alert
            type="info"
            showIcon
            message={
              <span>
                PDF preview may not work in all browsers.{' '}
                <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
                  <DownloadOutlined /> Download PDF
                </a>{' '}
                to view if preview doesn&apos;t load.
              </span>
            }
            style={{ margin: 8, flexShrink: 0 }}
          />
          <div style={{ flex: 1, position: 'relative' }}>
            {loading && (
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 1,
                }}
              >
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 48 }} />}
                />
              </div>
            )}
            <iframe
              src={previewUrl}
              title="Strategy Guide PDF"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              onLoad={() => {
                setLoading(false);
              }}
            />
          </div>
        </div>
      )}
    </Modal>
  );
}

export default StrategyGuidePreviewModal;
